// Iter 83.2 / Phase E4 Sprint 2 — Assets Dashboard.
// KPI-style snapshot showing fleet state distribution + recommended
// recovery totals (the latter feeds Sprint 3 FNF decisions).
import { useCallback, useEffect, useState } from "react";
import {
    Briefcase, Package, Warning, User, X, ArrowsClockwise,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const STATE_TONE = {
    Procured: "bg-zinc-100 text-zinc-700",
    InStock: "bg-emerald-100 text-emerald-800",
    Assigned: "bg-amber-100 text-amber-800",
    InUse: "bg-sky-100 text-sky-800",
    Damaged: "bg-rose-100 text-rose-700",
    Lost: "bg-rose-200 text-rose-900",
    WrittenOff: "bg-slate-200 text-slate-600",
};

const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TILE = ({ icon: Icon, label, value, tone, testid }) => (
    <div className={`p-4 rounded-xl border ${tone || "bg-white border-slate-200"}`} data-testid={testid}>
        <div className="flex items-center gap-2 text-xs uppercase text-slate-500 font-semibold">
            {Icon ? <Icon size={14} /> : null} {label}
        </div>
        <div className="mt-1 text-2xl font-semibold font-mono">{value}</div>
    </div>
);

export default function AssetsDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/assets/dashboard");
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load dashboard"));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="assets-dashboard-page">
            <PageHeader
                eyebrow="Asset Management"
                title="Assets Dashboard"
                description="Fleet state distribution · Recommended recoveries fed to Sprint 3 FNF"
                actions={(
                    <Button onClick={reload} size="sm" variant="outline" data-testid="assets-dash-refresh">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                )}
            />
            {loading ? (
                <div className="text-center py-12 text-sm text-slate-400">Loading…</div>
            ) : data ? (
                <>
                    <section className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <TILE icon={Package} label="All Assets" value={data.totals.all_assets}
                            tone="bg-white border-slate-200" testid="dash-all" />
                        <TILE icon={Package} label="In Stock" value={data.totals.in_stock}
                            tone="bg-emerald-50 border-emerald-200" testid="dash-stock" />
                        <TILE icon={User} label="With Employees" value={data.totals.out_with_employee}
                            tone="bg-sky-50 border-sky-200" testid="dash-out" />
                        <TILE icon={Warning} label="Damaged + Lost" value={data.totals.damaged + data.totals.lost}
                            tone="bg-rose-50 border-rose-200" testid="dash-attn" />
                    </section>
                    <section className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="dash-states">
                            <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                <Briefcase size={16} /> State distribution
                            </div>
                            <ul className="text-sm space-y-2">
                                {Object.entries(data.states).map(([state, count]) => (
                                    <li key={state} className="flex items-center justify-between">
                                        <Badge className={`text-[10px] ${STATE_TONE[state] || ""}`}>{state}</Badge>
                                        <span className="font-mono font-semibold">{count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="dash-recovery">
                            <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                <Warning size={16} /> Recommended recoveries (D18)
                            </div>
                            <div className="text-3xl font-mono font-bold">{fmtMoney(data.total_recommended_recoveries)}</div>
                            <p className="text-xs text-slate-500 mt-2">
                                Sum of operator-recommended recovery amounts on closed asset returns. This is the **default deduction** the Sprint 3 FNF engine will surface during clearance — operators can override per-asset at FNF approval with audit trail.
                            </p>
                            <p className="text-[10px] text-slate-400 mt-2">
                                D26 Asset Clearance Gate: FNF cannot reach Approved while any asset is OutWithEmployee unresolved.
                            </p>
                        </div>
                    </section>
                </>
            ) : (
                <div className="text-center py-12 text-sm text-slate-400">No data.</div>
            )}
        </div>
    );
}
